import _typeof from '@babel/runtime-corejs3/helpers/esm/typeof';
import _forEachInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/for-each';
import _Object$values from '@babel/runtime-corejs3/core-js-stable/object/values';
import _Object$freeze from '@babel/runtime-corejs3/core-js-stable/object/freeze';
import _slicedToArray from '@babel/runtime-corejs3/helpers/esm/slicedToArray';
import _Object$entries from '@babel/runtime-corejs3/core-js-stable/object/entries';
import _concatInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/concat';
import _Object$defineProperties from '@babel/runtime-corejs3/core-js-stable/object/define-properties';
import _Object$getOwnPropertyDescriptors from '@babel/runtime-corejs3/core-js-stable/object/get-own-property-descriptors';
import 'core-js/modules/es.regexp.exec.js';
import 'core-js/modules/es.string.split.js';

/**
 * Freezes an object recursively.
 * The object and its nested objects will no longer be possible to change.
 *
 * @remarks This function modifies the original object.
 * @param obj - The object to freeze
 * @returns The given object in a frozen state
 * @public
 *
 * @example
 * ```javascript
 * import { deepFreeze } from '@thisisbud/object-utils'
 *
 * const obj = {
 *   foo: 'bar',
 * }
 *
 * deepFreeze(obj)
 * ```
 */
function deepFreeze(obj) {
  if (obj !== null && _typeof(obj) === 'object') {
    var _context;

    _forEachInstanceProperty(_context = _Object$values(obj)).call(_context, deepFreeze);

    _Object$freeze(obj);
  }

  return obj;
}

/**
 * Deflate an object into a shallow depth object with keys in dot notation.
 * The values of the object will all be primitive data types.
 *
 * @param obj - An object to be deflated
 * @returns A deflated representation of the given object
 * @public
 *
 * @example
 * ```javascript
 * import { deflate } from '@thisisbud/object-utils'
 *
 * const obj = {
 *   a: 'foo',
 *   b: {
 *     c: 'bar',
 *   }
 * }
 *
 * deflate(obj)
 * // { 'a': 'foo', 'b.c': 'bar' }
 * ```
 */
function deflate(obj) {
  var _context;

  var output = {};

  _forEachInstanceProperty(_context = _Object$entries(obj)).call(_context, function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];

    if (value !== null && _typeof(value) === 'object') {
      var _context2;

      _forEachInstanceProperty(_context2 = _Object$entries(deflate(value))).call(_context2, function (_ref3) {
        var _context3;

        var _ref4 = _slicedToArray(_ref3, 2),
            nestedKey = _ref4[0],
            nestedValue = _ref4[1];

        output[_concatInstanceProperty(_context3 = "".concat(key, ".")).call(_context3, nestedKey)] = nestedValue;
      });

      return;
    }

    output[key] = value;
  });

  return output;
}

/**
 * Clones all enumerable own properties from one or more source objects to a target object.
 * With this method any getters and setters are kept intact.
 *
 * @param target - The object to apply the sources’ properties to, which is returned after it is modified
 * @param sources - Objects containing the properties you want to apply
 * @returns The target object with the properties from the source objects
 * @public
 *
 * @example
 * ```javascript
 * import { extend } from '@thisisbud/object-utils'
 *
 * const target = {
 *   a: 'foo',
 *   get b() {
 *     return 'bar'
 *   },
 * }
 * const source = {
 *   get a() {
 *     return 'foo'
 *   },
 * }
 *
 * extend(target, source)
 * // { get a() { return 'foo' }, get b() { return 'bar' }}
 * ```
 */
function extend(target) {
  for (var _len = arguments.length, sources = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    sources[_key - 1] = arguments[_key];
  }

  _forEachInstanceProperty(sources).call(sources, function (obj) {
    _Object$defineProperties(target, _Object$getOwnPropertyDescriptors(obj));
  });

  return target;
}

/**
 * Split a dot notation object key into its parts.
 *
 * @param key - An object key in dot notation
 * @returns And array of key parts
 */
function getKeyParts(key) {
  return key.split('.');
}
/**
 * Inflate a deflated object back to its former shape.
 *
 * @remarks Object types such as arrays will be converted to plain objects
 * @param obj - A deflated object to be inflated
 * @returns An inflated representation of the given object
 * @public
 *
 * @example
 * ```javascript
 * import { inflate } from '@thisisbud/object-utils'
 *
 * const obj = {
 *   'a': 'foo',
 *   'b.c': 'bar',
 * }
 *
 * inflate(obj)
 * // { a: 'foo', b: { c: 'bar' } }
 * ```
 */


function inflate(obj) {
  var _context;

  var output = {};

  _forEachInstanceProperty(_context = _Object$entries(obj)).call(_context, function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];

    var keyParts = getKeyParts(key);
    var lastKeyIndex = keyParts.length - 1;
    var currentChild = output;

    _forEachInstanceProperty(keyParts).call(keyParts, function (keyPart, keyPartIndex) {
      if (keyPartIndex === lastKeyIndex) {
        currentChild[keyPart] = value;
      } else {
        if (typeof currentChild[keyPart] === 'undefined') {
          currentChild[keyPart] = {};
        }

        currentChild = currentChild[keyPart];
      }
    });
  });

  return output;
}

export { deepFreeze, deflate, extend, inflate };
